/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:35:58AM]
Permissions to update outside the main branch: [false]
*/

import {
    Button,
    DocumentTitle,
    Form,
    passwordRule,
    requiredRule,
    useCondition,
    useErrorState,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { resetPasswordGet, resetPasswordPost } from 'axios/accountsApi';
import Window from 'components/Window';
import useApi from 'hooks/useApi';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardBody, Col, Row } from 'reactstrap';
import 'scss/Main.scss';

type ResetPasswordType = {
    password: string;
    repeatPassword: string;
};

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? '';
    const userId = searchParams.get('userId') ?? '';
    const navigate = useNavigate();
    const [disabled, setDisabled] = useState(true);
    const { designType } = useGeneralSettingsContext();

    const api = useApi();
    const { handleRequestError } = useErrorState();
    const { isLoading, mutate } = useMutation(
        (newPassword: string) => resetPasswordPost(api, token, userId, newPassword),
        {
            onError: (e) => handleRequestError(e),
            onSuccess: () => {
                navigate('/login');
            },
        }
    );

    const { isFetching } = useQuery(
        ['reset-password', token, userId],
        () => resetPasswordGet(api, token, userId),
        {
            onSuccess: () => setDisabled(false),
            onError: (e) => handleRequestError(e),
            refetchOnMount: false,
        }
    );

    return useCondition(
        designType,
        {
            nova: (
                <Window onSubmit={(data) => !disabled && mutate(data.password)} label={''}>
                    <Fields loading={isFetching || isLoading} disabled={disabled} />
                    <Button type="submit" isPending={isLoading} disabled={disabled}>
                        <span>
                            <Trans i18nKey="BackOfficeResetPassword" />
                        </span>
                    </Button>
                </Window>
            ),
        },
        <DocumentTitle code="BackOfficePasswordReset">
            <div className="AuthForm">
                <Row>
                    <Col sm="3" />
                    <Col sm="6">
                        <Card>
                            <CardBody>
                                <Form<ResetPasswordType>
                                    clearAfterSubmit={false}
                                    onSubmit={(data) => !disabled && mutate(data.password)}
                                >
                                    <Fields loading={isFetching || isLoading} disabled={disabled} />
                                    <Button type="submit" isPending={isLoading} disabled={disabled}>
                                        <span>
                                            <Trans i18nKey="BackOfficeResetPassword" />
                                        </span>
                                    </Button>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </DocumentTitle>
    );
};

const Fields = ({ disabled, loading }: { disabled: boolean; loading: boolean }) => {
    const { t } = useTranslation();
    const { getValues } = useFormContext<ResetPasswordType>();
    return (
        <>
            <ValidatedInput<ResetPasswordType>
                data-testid="input-password"
                name="password"
                type="password"
                rules={{ required: requiredRule, pattern: passwordRule }}
                label={t('BackOfficeNewPassword')}
                loading={loading}
                disabled={disabled}
            />
            <ValidatedInput<ResetPasswordType>
                data-testid="input-repeat-password"
                name="repeatPassword"
                type="password"
                rules={{
                    required: requiredRule,
                    validate: (val) =>
                        val === getValues().password || 'BackOfficePasswordConfirmMismatch',
                }}
                label={t('BackOfficeRepeatNewPassword')}
                loading={loading}
                disabled={disabled}
            />
        </>
    );
};

export default ResetPassword;
